import { useEffect, useId, useState } from "react";
import * as api from "../api/client";
import { useEditor } from "../state/editor";
import { validarCotizacion } from "../domain/pricing";
import { construirPayload, construirPdf } from "../domain/contracts";
import {
  CANALES,
  CANALES_ENVIO,
  FORMATO_RESUMEN,
  FORMATOS_PDF,
  NOMBRE_CANAL,
  columnasResumen,
} from "../domain/model";
import type { Vendedor } from "../domain/types";
import { Campo, ErrorAviso, Modal, Numero, Porcentaje } from "./ui";
import { usePreguntarSitiosEnCero } from "./Dialogos";
import ListaGarantias from "./ListaGarantias";
export default function Margen({ vendedor }: { vendedor: Vendedor }) {
  const c = useEditor((s) => s.cotizacion),
    patch = useEditor((s) => s.patch),
    [preview, setPreview] = useState(false);
  return (
    <section className="card">
      <p className="eyebrow">Paso 3</p>
      <h2>Margen y envío</h2>
      <div className="grid two">
        <Porcentaje
          label="Descuento general (%)"
          value={c.descuento}
          max={100}
          onChange={(n) => patch({ descuento: n ?? 0 })}
        />
        <Campo label="Formato de entrega">
          <select
            // Cotizaciones guardadas con "por_sitio" (opción retirada) salían
            // igual al consolidado, así que se muestran como tal.
            value={
              FORMATOS_PDF.some((f) => f.valor === c.formatoPdf)
                ? c.formatoPdf
                : "consolidado"
            }
            onChange={(e) => patch({ formatoPdf: e.target.value })}
          >
            {FORMATOS_PDF.map((f) => (
              <option key={f.valor} value={f.valor}>
                {f.etiqueta}
              </option>
            ))}
          </select>
        </Campo>
        {c.formatoPdf === FORMATO_RESUMEN ? (
          <p className="muted">
            Una fila por sitio y una columna por zona:{" "}
            {columnasResumen(c.sitios).join(" · ") || "sin productos todavía"}.
            Agrupa zonas con el campo «Columna en resumen» de cada producto
            (paso 2). Los sitios sin piezas salen en $0.
          </p>
        ) : null}
        <label className="check">
          <input
            type="checkbox"
            checked={c.ivaIncluido}
            onChange={(e) => patch({ ivaIncluido: e.target.checked })}
          />
          Incluir IVA
        </label>
        {/* El resumen por tienda y zona no tiene detalle por pieza. */}
        {c.formatoPdf !== FORMATO_RESUMEN ? (
          <label className="check">
            <input
              type="checkbox"
              checked={c.mostrarValorUnitario !== false}
              onChange={(e) =>
                patch({ mostrarValorUnitario: e.target.checked })
              }
            />
            Mostrar valor unitario en el PDF
          </label>
        ) : null}
        {c.ivaIncluido ? (
          <Numero
            label="IVA (%)"
            value={c.ivaPct}
            max={100}
            onChange={(n) => patch({ ivaPct: n ?? 0 })}
          />
        ) : null}
        <FormaDePago
          value={c.condiciones.forma_pago}
          onChange={(forma_pago) =>
            patch({ condiciones: { ...c.condiciones, forma_pago } })
          }
        />
        <Campo label="Tiempo de entrega">
          <input
            value={c.condiciones.tiempo_entrega}
            onChange={(e) =>
              patch({
                condiciones: {
                  ...c.condiciones,
                  tiempo_entrega: e.target.value,
                },
              })
            }
          />
        </Campo>
        <Campo label="Validez de la oferta">
          <input
            value={c.condiciones.validez_oferta}
            onChange={(e) =>
              patch({
                condiciones: {
                  ...c.condiciones,
                  validez_oferta: e.target.value,
                },
              })
            }
          />
        </Campo>
      </div>
      <details>
        <summary>Garantías</summary>
        <label className="check">
          <input
            type="checkbox"
            checked={c.incluirGarantias}
            onChange={(e) => patch({ incluirGarantias: e.target.checked })}
          />
          Incluir garantías en la cotización
        </label>
        {c.incluirGarantias ? (
          <>
            <ListaGarantias
              lista={c.garantias}
              onChange={(garantias) => patch({ garantias })}
              conCheck
            />
            <p className="muted">
              Marca solo las garantías que ofreces; cada una necesita sus años.
              Aplican a toda la cotización. Las de un sitio o una pieza se
              agregan en el paso 2, en el sitio o en la pieza.
            </p>
            <p className="notice">
              Garantía contra defectos de fábrica. No cubre mal uso, desgaste
              natural, manipulación de terceros ni fuerza mayor.
            </p>
          </>
        ) : null}
      </details>
      <div className="row">
        <button onClick={() => setPreview(true)}>
          Vista previa de la cotización
        </button>
      </div>
      <fieldset className="channel">
        <legend>Entregar por</legend>
        {CANALES.map((canal) => (
          <label className="check" key={canal}>
            <input
              name="canal"
              type="radio"
              checked={c.canal === canal}
              onChange={() => patch({ canal })}
            />
            {NOMBRE_CANAL[canal]}
          </label>
        ))}
      </fieldset>
      {c.canal !== "pdf" ? (
        <>
          <p className="muted">
            {c.contacto
              ? `Contacto seleccionado: ${c.contacto.nombre}`
              : "Selecciona un contacto en el paso Cliente para enviar."}
          </p>
          {CANALES_ENVIO[c.canal].includes("correo") ? (
            <Campo label="Correo de envío">
              <input
                type="email"
                value={c.correoEnvio}
                onChange={(e) => patch({ correoEnvio: e.target.value })}
              />
              <small className="muted">
                Viene del contacto; puedes cambiarlo (por ejemplo, al correo de
                compras de la empresa). Le llega directo al cliente.
              </small>
            </Campo>
          ) : null}
          {CANALES_ENVIO[c.canal].includes("whatsapp") ? (
            // Oct-2026: la cotización le llega al COMERCIANTE (su contacto
            // del CRM) y él se la reenvía al cliente desde su WhatsApp. Se
            // muestra su número, no el del cliente.
            <div className="field dato-fijo">
              <span>WhatsApp</span>
              {vendedor.whatsapp === null ? (
                <p className="notice" role="status">
                  Tu usuario todavía no tiene su WhatsApp configurado en el CRM.
                  Pide a Invers que lo asigne; mientras tanto usa «Solo PDF» o
                  «Correo».
                </p>
              ) : (
                <p>
                  Te llega a tu WhatsApp
                  {vendedor.whatsapp?.telefono
                    ? ` (${vendedor.whatsapp.telefono})`
                    : ""}
                </p>
              )}
              <small className="muted">
                Revísala y reenvíasela al cliente desde tu WhatsApp. El cliente
                no recibe nada desde el número central de Diez Visual.
              </small>
            </div>
          ) : null}
        </>
      ) : null}
      {preview ? (
        <VistaPreviaPdf vendedor={vendedor} onClose={() => setPreview(false)} />
      ) : null}
    </section>
  );
}

// Vista previa = el PDF real, idéntico al que se descarga: n8n recalcula de
// forma autoritativa (cotizador-confirmar, sin efectos) y genera el PDF con
// el mismo workflow de entrega (ver api.pdfVistaPrevia: sin GHL, sin
// guardar). Nada de maquetas HTML locales que se desfasan del documento.
export function VistaPreviaPdf({
  vendedor,
  onClose,
}: {
  vendedor: Vendedor;
  onClose: () => void;
}) {
  const [url, setUrl] = useState(""),
    [error, setError] = useState<unknown>(),
    preguntarSitiosEnCero = usePreguntarSitiosEnCero();
  useEffect(() => {
    const ctrl = new AbortController();
    let objeto = "";
    (async () => {
      try {
        // Se cede un turno antes de pedir nada: en desarrollo StrictMode monta,
        // desmonta y vuelve a montar el efecto de inmediato, y sin esto el
        // primer montaje (ya cancelado) alcanzaba a mandar un cálculo a n8n.
        await Promise.resolve();
        if (ctrl.signal.aborted) return;
        const c = structuredClone(useEditor.getState().cotizacion),
          errores = validarCotizacion(c);
        if (errores.length) throw new Error(errores.join(" "));
        const r = await api.calcular(
          construirPayload(c, vendedor, crypto.randomUUID()),
          ctrl.signal,
        );
        if (ctrl.signal.aborted) return;
        const incluirSitiosEnCero = await preguntarSitiosEnCero(c, r);
        if (ctrl.signal.aborted) return;
        const blob = await api.pdfVistaPrevia(
          construirPdf(c, r, vendedor, { incluirSitiosEnCero }),
          ctrl.signal,
        );
        if (ctrl.signal.aborted) return;
        objeto = URL.createObjectURL(blob);
        setUrl(objeto);
      } catch (e) {
        if (!ctrl.signal.aborted) setError(e);
      }
    })();
    return () => {
      ctrl.abort();
      if (objeto) URL.revokeObjectURL(objeto);
    };
  }, [vendedor, preguntarSitiosEnCero]);
  return (
    <Modal
      title="Vista previa de la cotización"
      onClose={onClose}
      className="pdf-preview"
    >
      <ErrorAviso error={error} />
      {url ? (
        <>
          <iframe
            className="pdf-frame"
            src={url}
            title="PDF de la cotización"
          />
          <p className="muted">
            ¿No se ve?{" "}
            <a href={url} target="_blank" rel="noreferrer">
              Abrir el PDF en otra pestaña
            </a>
          </p>
        </>
      ) : !error ? (
        <p role="status">Generando el PDF con precios validados…</p>
      ) : null}
    </Modal>
  );
}

// Las tres opciones salen de cotizaciones reales de Diez Visual (ver los
// ejemplos de `condiciones.forma_pago` en legacy/cotizador.js). `forma_pago`
// sigue siendo texto libre en el contrato, así que las cotizaciones guardadas
// con cualquier otra redacción se siguen abriendo — caen en "Otra".
const FORMAS_DE_PAGO = [
  "Contado",
  "50% anticipo, 50% contra entrega",
  "30 días",
];
function FormaDePago({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  // Una cotización guardada con otra redacción abre en "Otra" con su texto.
  const [otra, setOtra] = useState(
    () => value !== "" && !FORMAS_DE_PAGO.includes(value),
  );
  return (
    <>
      <Campo label="Forma de pago (obligatoria para entregar)">
        <select
          required
          aria-invalid={!value.trim()}
          value={otra ? "otra" : FORMAS_DE_PAGO.includes(value) ? value : ""}
          onChange={(e) => {
            setOtra(e.target.value === "otra");
            // Al pasar a "Otra" se limpia la forma anterior para que el
            // texto libre arranque vacío; si ya estaba en "Otra" se conserva.
            onChange(
              e.target.value === "otra" ? (otra ? value : "") : e.target.value,
            );
          }}
        >
          <option value="">Seleccionar forma de pago</option>
          {FORMAS_DE_PAGO.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
          <option value="otra">Otra…</option>
        </select>
      </Campo>
      {otra ? (
        <Campo label="Otra forma de pago">
          <textarea
            placeholder="Ej: 40% anticipo, 60% a 30 días"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </Campo>
      ) : null}
    </>
  );
}
